import { showDeleteConfirmation } from '@modules/common';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { CopyTemplateRequest, BlockTemplateResponse } from '@business/entities/proposal';
import { ProposalTemplateAPI } from '@business/api/proposal_template_api';
import { switchMap, tap, map } from 'rxjs/operators';

export class BlockTemplateBloc extends BaseBloc {
  private readonly templatesRepo = new Repository<BlockTemplateResponse>({
    getItemId: item => item.id,
  });

  readonly templates$ = this.templatesRepo.items$.pipe(map(templates => templates.filter(t => !t.isUniversal)));
  readonly universalTemplates$ = this.templatesRepo.items$.pipe(map(templates => templates.filter(t => t.isUniversal)));
  readonly selectItem = this.templatesRepo.selectItem;

  onReset() {
    this.templatesRepo.reset();
  }

  fetchTemplates = (type: string) => {
    return ProposalTemplateAPI.fetchBlockTemplates(type).pipe(
      this.templatesRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_proposal_template_error') }),
    );
  };

  fetchUniversalSections = () => {
    return ProposalTemplateAPI.fetchBlockTemplates('proposal').pipe(
      this.templatesRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_proposal_template_error') }),
    );
  };

  deleteTemplate = (id: string) => {
    return showDeleteConfirmation('Delete Template', `Do you really want to remove this template?`).pipe(
      switchMap(() => ProposalTemplateAPI.deleteBlockTemplate(id)),
      this.templatesRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_proposal_template'),
      }),
    );
  };

  copyBlockTemplate = (data: CopyTemplateRequest) => {
    return ProposalTemplateAPI.copyBlockTemplate(data).pipe(
      handleMessage({
        type: requestMessage('copy_proposal_template_block'),
      }),
    );
  };

  saveAsUniversal = (data: { name: string; type: string; variation: string; data: any; isSection: boolean }) => {
    return ProposalTemplateAPI.saveBlockTemplate({
      ...data,
      is_universal: true,
    }).pipe(
      this.templatesRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_universal_section'),
      }),
    );
  };

  createBlockTemplate = this.saveAsUniversal;

  updateBlockTemplate = (id: string, data: BlockTemplateResponse) => {
    return ProposalTemplateAPI.updateBlockTemplate(id, data).pipe(
      this.templatesRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_universal_section'),
      }),
    );
  };

  deleteUniversalSection = (id: string) => {
    return showDeleteConfirmation(
      'Delete Universal Section',
      'This will unlink all connected sections across all proposal templates. This action cannot be undone.',
    ).pipe(
      switchMap(() => ProposalTemplateAPI.deleteBlockTemplate(id)),
      tap(() => {
        this.templatesRepo.removeOne(id);
      }),
      handleMessage({
        type: requestMessage('delete_universal_section'),
      }),
    );
  };
}
